import {useMutation, useQueryClient} from '@tanstack/react-query';
import {FlagPicker} from './Flag';
import {getApiErrorMessage} from '@/api/client';
import {type ChannelAggregateList, itemsApi} from '@/api/items';
import {type FlagColor} from '@/api/flags';
import {DEFAULT_AFFILIATE, PLATFORM_ID} from '@/lib/platform';
import {showToast} from '@/lib/toast';

// Every cached channel aggregate: the Content page's Channels tab and a job's or folder's
// "Group by channel" (queryKeys.allChannels / jobChannels / folderChannels).
const isChannelAggregateQuery = ({queryKey}: {queryKey: readonly unknown[]}) =>
  (queryKey[0] === 'items' && queryKey[1] === 'channels') || queryKey[2] === 'channels';

type Change = {color: FlagColor | null; note: string | null; noteOnly?: boolean};

// A channel's flag lives on the channel's own actor item, as on the Single Submission page: resolving
// it makes that item real (or finds it), so the flag shows in My flagged items and a later Classify
// channel lands on the same item.
async function resolveActorItem(authorId: string, label: string) {
  const {item_id: id} = await itemsApi.resolve({
    item_id: crypto.randomUUID(),
    external_id: authorId,
    author_id: authorId,
    author_name: label,
    title: label,
    metadata: {},
    platform: PLATFORM_ID,
    affiliate: DEFAULT_AFFILIATE,
    submission_type: 'Actor'
  });
  return id;
}

// The flag on a channel group header. Its value comes from the channel aggregate (flag/flag_note),
// patched into every cached aggregate first so the picker answers at once, and rolled back if the
// write fails.
export function ChannelFlagPicker({
  authorId,
  label,
  flag,
  note
}: {
  readonly authorId: string;
  readonly label: string;
  readonly flag: FlagColor | null;
  readonly note: string | null;
}) {
  const qc = useQueryClient();
  const {mutate} = useMutation({
    async mutationFn(change: Change) {
      const id = await resolveActorItem(authorId, label);
      await (change.noteOnly
        ? itemsApi.setFlagNote(id, change.note)
        : itemsApi.setFlags([id], change.color, change.note));
    },
    async onMutate(change) {
      await qc.cancelQueries({predicate: isChannelAggregateQuery});
      const previous = qc.getQueriesData<ChannelAggregateList>({
        predicate: isChannelAggregateQuery
      });
      qc.setQueriesData<ChannelAggregateList>({predicate: isChannelAggregateQuery}, (list) =>
        list
          ? {
              results: list.results.map((c) =>
                c.author_id === authorId
                  ? {
                      ...c,
                      flag: change.color,
                      // A recolour keeps the note, as the server does; clearing drops it.
                      flag_note: change.noteOnly
                        ? change.note
                        : change.color
                          ? (change.note ?? c.flag_note ?? null)
                          : null
                    }
                  : c
              )
            }
          : list
      );
      return {previous};
    },
    onError(error, change, context) {
      for (const [key, data] of context?.previous ?? []) {
        qc.setQueryData(key, data);
      }

      showToast(
        getApiErrorMessage(
          error,
          change.color ? 'Failed to flag channel.' : 'Failed to clear flag.'
        ),
        'error'
      );
    },
    async onSettled() {
      // My flagged items lists the actor item too.
      await qc.invalidateQueries({queryKey: ['items']});
    }
  });

  return (
    <FlagPicker
      value={flag}
      note={note}
      subject={label}
      onChange={(color, newNote) => {
        mutate({color, note: newNote ?? null});
      }}
      onNoteChange={(newNote) => {
        mutate({color: flag, note: newNote, noteOnly: true});
      }}
    />
  );
}
